import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAccount } from '../../contexts/AccountContext';
import { apiErrorMessage } from '../../utils/apiError';
import mcpServiceService from '../../services/mcpServiceService';
import WizardStepper from '../WizardStepper';
import WizardHotelStep from '../WizardHotelStep';
import WizardReview from '../WizardReview';
import { GROUPS, HOTEL_DEFAULTS, HotelFieldGroup, toPayload } from './McpHotelFields';
import '../AddPaymentPorterWizard.css';
import './McpServicePage.css';

// SET UP MCP AS A SERVICE FOR A HOTEL — /mcp-service/new (?hotelId= to start on one). Hotel → PMS → Booking policy →
// Guest emails & content → Review, in the same wizard shape as the other product creates (Payment Porter, Universal API
// access): the shared stepper, the shared searchable hotel cards (WizardHotelStep), choice cards for the PMS, and the
// shared review list (WizardReview). The middle steps draw the same field groups as the hotel's Settings tab
// (McpHotelFields), and the last one is the same PUT /hotels/{hotelId} the tab saves with — creating the
// mcp_hotel_settings row is what puts the hotel on the list. Then it opens the hotel.
//
// Only hotels without the service and that this person may change (canEdit, a hotel admin) are offered.
//
// THE PMS is one of the systems the service supports as sources (V643, Admin › Platform › MCP as a Service — each a
// Marketplace system), saved as mcp_source_system_id (V648). Its pipeline templates are what the hotel's offering is
// built from. No credential and no OPERA codes here: they belong to the offering, and stay on the Settings tab.

const STEPS = [
  { num: 1, label: 'Hotel' },
  { num: 2, label: 'PMS' },
  { num: 3, label: 'Booking policy' },
  { num: 4, label: 'Guest emails & content' },
  { num: 5, label: 'Review' },
];
const REVIEW = STEPS.length;
const STEP_GROUPS = { 3: ['booking'], 4: ['emails', 'content'] };
const groupOf = (k) => GROUPS.find(g => g.key === k);
// The console's hotel entry in the shape WizardHotelStep draws: the name, then the inncode (and city) beneath it. The code
// is left off when the name IS the code, as on hotels created from an inncode alone.
const asPickerHotel = (h) => ({ hotelid: h.hotelId, hotelname: h.hotelName,
  hotelcode: [h.hotelCode !== h.hotelName && h.hotelCode, h.city].filter(Boolean).join(' · ') });

export default function McpHotelWizard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { selectedAccount } = useAccount();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1);
  const [hotelId, setHotelId] = useState(searchParams.get('hotelId') || '');
  const [form, setForm] = useState(HOTEL_DEFAULTS);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!selectedAccount) return;
    try { setData(await mcpServiceService.getAccount(selectedAccount)); } catch (e) { setError(apiErrorMessage(e)); }
  }, [selectedAccount]);
  useEffect(() => { load(); }, [load]);

  const candidates = data ? data.hotels.filter(h => !h.settings && h.canEdit) : [];
  const hotel = candidates.find(h => String(h.hotelId) === String(hotelId)) || null;
  const sources = (data && data.sources) || [];
  const pms = sources.find(x => String(x.id) === String(form.mcp_source_system_id)) || null;

  const problem = () => {
    if (step === 1 && !hotel) return 'Choose the hotel';
    if (step === 2 && !pms) return 'Choose the hotel’s PMS';
    return null;
  };
  const next = () => {
    const p = problem();
    if (p) { setError(p); return; }
    setError(null);
    setStep(s => s + 1);
  };
  const back = () => { setError(null); if (step === 1) navigate('/mcp-service'); else setStep(s => s - 1); };
  const create = async () => {
    setSaving(true);
    setError(null);
    try {
      await mcpServiceService.saveHotel(hotel.hotelId, toPayload(form));
      navigate(`/mcp-service/hotels/${hotel.hotelId}`);
    } catch (e) {
      setError(apiErrorMessage(e));
      setSaving(false);
    }
  };

  const yesNo = (v) => (v === 'Y' ? 'Yes' : 'No');
  const review = hotel ? [
    ['Hotel', hotel.hotelName],
    ['PMS', pms && pms.name],
    ['Status when created', form.service_status === 'ACTIVE' ? 'Live' : 'Paused'],
    ['Agents may book / cancel', `${yesNo(form.booking_enabled)} / ${yesNo(form.cancel_enabled)}`],
    ['Hold while paying', `${form.hold_minutes} minutes`],
    ['Longest stay', `${form.max_nights} nights`],
    ['Email sender name', form.sender_name || hotel.hotelName],
    ['Guest replies go to', form.reply_to_email],
    ['Hotel website', form.website_url],
  ] : [];

  const footer = (
    <div className="ppw-footer">
      <button type="button" className="ppw-btn-back" onClick={back}>← Back</button>
      {step < REVIEW
        ? <button type="button" className="ppw-btn-next" onClick={next}>Next →</button>
        : <button type="button" className="ppw-btn-next" onClick={create} disabled={saving}>{saving ? 'Setting up…' : 'Set up the hotel'}</button>}
    </div>
  );
  const card = (title, subtitle, body) => (
    <div className="ppw-card">
      <div className="ppw-card-header">
        <h2 className="ppw-card-title">{title}</h2>
        {subtitle && <p className="ppw-card-subtitle">{subtitle}</p>}
      </div>
      <div className="ppw-card-body">
        {body}
        {error && <span className="ppw-error-msg">{error}</span>}
      </div>
      {footer}
    </div>
  );

  return (
    <div className="ppw-page">
      <WizardStepper steps={STEPS} currentStep={step} />
      {!selectedAccount && <div className="os-msg">Choose a chain in the navigation bar.</div>}
      {selectedAccount && !data && !error && <p>Loading…</p>}
      {!data && error && <div className="os-msg os-msg-error">{error}</div>}

      {data && step === 1 && (
        <WizardHotelStep
          hotels={candidates.map(asPickerHotel)}
          selectedHotel={hotel && asPickerHotel(hotel)}
          onSelect={h => { setHotelId(String(h.hotelid)); setError(null); }}
          error={error}
          title="Choose Hotel"
          subtitle="The hotel to offer to AI assistants. Hotels already running the service are not listed."
          emptyMessage="Every hotel you can set up in this chain already runs MCP as a Service."
          onBack={back}
          onNext={next}
        />
      )}

      {data && hotel && step === 2 && card('PMS',
        'The property system the hotel runs. The service is built from the pipelines made for it.',
        <div className="ppw-connector-list">
          {sources.length ? sources.map(x => (
            <button key={x.id} type="button"
                    className={`ppw-connector-card${pms && pms.id === x.id ? ' selected' : ''}`}
                    onClick={() => { setForm(f => ({ ...f, mcp_source_system_id: String(x.id) })); setError(null); }}>
              <div className="ppw-connector-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" stroke="#4a6cf7" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="ppw-connector-info">
                <div className="ppw-connector-name">{x.name}</div>
                {x.company && <div className="ppw-connector-meta">{x.company}</div>}
              </div>
            </button>
          )) : (
            <div className="ppw-empty-state">
              <strong>No PMS is supported yet</strong>
              A superadmin adds them under Admin › Platform › MCP as a Service.
            </div>
          )}
        </div>)}

      {data && hotel && STEP_GROUPS[step] && card(STEPS[step - 1].label,
        'All of these can be changed afterwards on the hotel’s Settings tab.',
        <div className="mcps-wizard-fields">
          {STEP_GROUPS[step].map(k => (
            <section key={k} className="mcps-wizard-section">
              {STEP_GROUPS[step].length > 1 && <h3 className="mcps-wizard-section-title">{groupOf(k).title}</h3>}
              <p className="ppw-card-subtitle">{groupOf(k).hint}</p>
              <HotelFieldGroup group={k} form={form} setForm={setForm} edit hotel={hotel} />
            </section>
          ))}
        </div>)}

      {data && hotel && step === REVIEW && card('Review',
        'Check the settings, then set the hotel up. Everything can be changed afterwards on its Settings tab.',
        <WizardReview rows={review} />)}
    </div>
  );
}
